import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, getRefreshToken, useAuthStore } from '../stores/useAuthStore';

const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:4100/api/v1';

const api = axios.create({ baseURL });

// Request Interceptor — attach the current access token
api.interceptors.request.use(
  config => {
    const token = getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  error => Promise.reject(error)
);

// --- Silent refresh on 401 -------------------------------------------------
// A single in-flight refresh is shared across all requests that 401 at once,
// so we never fire N parallel /auth/refresh calls (which would each rotate the
// token and invalidate the others).

let refreshPromise: Promise<string> | null = null;

// `refreshPromise` only dedupes within one tab. Refresh tokens are one-time-use
// server-side, so two tabs presenting the same one gets the loser's session
// revoked as a replay. A cross-tab lock serialises them; browsers without the
// Web Locks API fall back to the recovery path in refreshAccessToken.
async function withRefreshLock(run: () => Promise<string>): Promise<string> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    // lib.dom types the granted callback as returning T rather than
    // PromiseLike<T>, so this nests; the runtime does await it.
    return await navigator.locks.request('invoiceflow:token-refresh', run);
  }
  return run();
}

async function refreshAccessToken(): Promise<string> {
  // Read at call time, not from a captured snapshot: a sibling tab may have
  // rotated the token since this module loaded.
  const seen = getRefreshToken();
  if (!seen) {
    throw new Error('No refresh token');
  }

  return withRefreshLock(async () => {
    // Whoever held the lock before us may have rotated already; their result is
    // in localStorage and is just as good as one we'd fetch ourselves.
    const adopted = adoptSiblingRotation(seen);
    if (adopted) return adopted;

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    try {
      // Bare axios (not `api`) so this request never re-enters the interceptor.
      const response = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
      const tokens = response.data?.data?.tokens;
      if (!tokens?.accessToken || !tokens?.refreshToken) {
        throw new Error('Malformed refresh response');
      }

      useAuthStore.getState().setTokens(tokens.accessToken, tokens.refreshToken);
      return tokens.accessToken;
    } catch (error) {
      // Unlocked fallback: if the stored token changed while ours was in
      // flight, a sibling tab won the race and stored a working pair.
      const rescued = adoptSiblingRotation(refreshToken);
      if (rescued) return rescued;
      throw error;
    }
  });
}

// If the stored refresh token is no longer the one we were about to present,
// another tab rotated it — return that tab's access token instead of spending
// (and burning) ours.
function adoptSiblingRotation(presented: string): string | null {
  if (getRefreshToken() === presented) return null;
  return getAccessToken();
}

// Coalesce concurrent refreshes into one, and clear the slot exactly once when
// it settles — a per-waiter reset would let a request that 401s mid-flight
// start a second, redundant rotation.
function refreshOnce(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Only a refusal by the server means the session is over. A network drop, a
// 502 during a deploy, or a 429 from the rate limiter says nothing about
// whether the refresh token is still good — logging out on those is what makes
// the app feel like it signs you out at random.
function isSessionRejected(error: unknown): boolean {
  const status = (error as AxiosError)?.response?.status;
  return status === 400 || status === 401 || status === 403;
}

// Response Interceptor
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    const isAuthEndpoint =
      original?.url?.includes('/auth/refresh') || original?.url?.includes('/auth/login');

    if (error.response?.status === 401 && original && !original._retry && !isAuthEndpoint) {
      // No refresh token → session is truly over.
      if (!getRefreshToken()) {
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }

      original._retry = true;

      try {
        const newToken = await refreshOnce();

        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshError) {
        if (isSessionRejected(refreshError) || !getRefreshToken()) {
          useAuthStore.getState().logout();
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
